import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import { useRef, useState } from "react";
import { Button, SelectOption } from "@rio-cloud/rio-uikit";

import { NameInputChange } from "@components/Dialog/Receipt/form";
import { formatCurrency, formatFileName } from "@utils/format";
import InputSelect from "@components/Dialog/Receipt/select";
import { allowedTypes, MAX_SIZE } from "@utils/file";
import moment from "moment";

const newOption = {
  id: "other",
  label: "Cadastrar novo(a)…",
};

type FormState = {
  date: string;
  value: string;
  branch: string;
  segment: string;
  supplier: string;
  trip: string;
  vehicle: string;
  driver: string[];
  category: string;
  subcategory: string;
  observation: string;
};

interface IExpenseFormProps {
  branchs: SelectOption[];
  segments: SelectOption[];
  suppliers: SelectOption[];
  drivers: SelectOption[];
  categories: SelectOption[];
  vehicles: SelectOption[];
  subCategories: SelectOption[];
  fileName?: string;
  openFormModal: (toggle: boolean) => void;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  onCreateNewOption: (name: NameInputChange) => void;
  onSave: (form: FormState) => void;
  isEdit?: boolean;
  handleSubmitNew?: () => void;
  handleFile?: (file: File) => void;
  handleClearFile: () => void;
  changeFileName: (fileName: string) => void;
}

const ExpenseForm = ({
  branchs,
  segments,
  suppliers,
  drivers,
  categories,
  vehicles,
  subCategories,
  openFormModal,
  form,
  setForm,
  onCreateNewOption,
  onSave,
  isEdit,
  handleSubmitNew,
  handleFile,
  handleClearFile,
  fileName,
  changeFileName,
}: IExpenseFormProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [driversQuantity, setDriversQuantity] = useState(form?.driver?.length || 1);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [errorFile, setErrorFile] = useState("");

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const validateFile = (file: File | null): string => {
    if (!file) return "";

    if (file.size > MAX_SIZE) {
      return "O arquivo excede o tamanho máximo de 10 MB.";
    }

    if (!allowedTypes.includes(file.type)) {
      return "Formato não permitido. Use JPEG, PNG, GIF, WebP, PDF, DOC, DOCX, XLS ou XLSX.";
    }

    return "";
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    const name = formatFileName(file?.name);
    const validation = validateFile(file);

    if (!!validation) {
      setErrorFile(validation);
      changeFileName("");
      return;
    }

    if (file) {
      setErrorFile("");
      changeFileName(name);
      // Create a new File object with the formatted name (spread doesn't work on File objects)
      const renamedFile = new File([file], name, { type: file.type, lastModified: file.lastModified });
      handleFile && handleFile(renamedFile);
    }
  };

  const inputChange = (name: NameInputChange, value?: string) => {
    if (name === "value" && value) {
      const formmated = formatCurrency(value, false);
      setForm((prev) => ({
        ...prev,
        [name]: formmated,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const inputDriverChange = (value?: string, index?: number) => {
    if (!value) return;

    setForm((prev) => {
      const newDrivers = [...prev.driver];

      // Se o índice existe e já tem um valor, SUBSTITUI (edição)
      // Se o índice não existe ou está vazio, ADICIONA (novo)
      if (index !== undefined && index < newDrivers.length) {
        newDrivers[index] = value; // SUBSTITUI no índice
      } else {
        newDrivers.push(value); // ADICIONA no final
      }

      return {
        ...prev,
        driver: newDrivers,
      };
    });
  };

  const inputClearDriver = (idx: number) => {
    setForm((prev) => {
      const newDrivers = prev.driver.filter((_, i) => i !== idx);

      return {
        ...prev,
        driver: newDrivers,
      };
    });

    setDriversQuantity((prev) => prev - 1);
  };

  const handleDateChange = (value: string | any, isValid: boolean) => {
    setForm((prev) => ({
      ...prev,
      date: typeof value === "string" ? value : value.format("YYYY-MM-DD"),
    }));

    setErrors((prev) => ({
      ...prev,
      date: "",
    }));
  };

  const handleSubmit = () => {
    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(form);
  };

  const validate = () => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};

    if (!form.date) newErrors.date = "Informe a data";

    if (!form.value) newErrors.value = "Informe o valor";
    else if (Number(form.value) <= 0) newErrors.value = "Valor deve ser maior que zero";

    if (!form.branch) newErrors.branch = "Selecione a filial";
    else if (form.branch === "Selecione") newErrors.branch = "Selecione a filial";

    if (!form.category) newErrors.category = "Selecione a categoria";

    return newErrors;
  };

  const inputProps1: React.HTMLProps<HTMLInputElement> = {
    placeholder: "Selecionar a data",
    id: "2",
  };

  if (!form.date) {
    inputProps1.value = "";
  }

  const fileClear = () => {
    changeFileName("");
    handleClearFile();
  };

  return (
    <>
      <div className="padding-x-20">
        <form>
          <fieldset>
            <div className="">
              <div className={`form-group ${errors.date ? "has-feedback has-error" : ""}`}>
                <label htmlFor="1" className="display-flex flex-wrap justify-content-between">
                  <span>Qual a data?*</span>
                </label>
                <DatePicker
                  id="2"
                  dateFormat="DD/MM/YYYY"
                  onChange={handleDateChange}
                  inputProps={inputProps1}
                  value={form.date === "" ? "" : moment(form.date).format("DD/MM/YYYY")}
                  className="margin-0"
                  timeFormat={false}
                />
                {errors.date ? <span className="form-control-feedback rioglyph rioglyph-error-sign"></span> : null}
                <span className="help-block">
                  <span>{errors.date}</span>
                </span>
              </div>
              <div className={`form-group ${errors.value ? "has-feedback has-error" : ""}`}>
                <label
                  htmlFor="inputValue"
                  className="display-inline-flex flex-wrap justify-content-between width-100pct"
                >
                  Qual o valor?*
                </label>
                <div className="input-group">
                  <span className="input-group-addon">R$</span>
                  <input
                    id="inputValue"
                    type="text"
                    className="form-control"
                    value={form.value}
                    onChange={(e) => inputChange("value", e.target.value)}
                    name="value"
                  />
                </div>
                {errors.value ? <span className="form-control-feedback rioglyph rioglyph-error-sign"></span> : null}
                <span className="help-block">
                  <span>{errors.value}</span>
                </span>
              </div>

              <InputSelect
                inputChange={inputChange}
                label="Qual a filial?*"
                name="branch"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={branchs}
                value={form.branch}
                errorMessage={errors.branch}
              />

              <InputSelect
                inputChange={inputChange}
                label="Qual o segmento?"
                name="segment"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={segments}
                value={form.segment}
                errorMessage={errors.segment}
              />

              <InputSelect
                inputChange={inputChange}
                label="Qual o fornecedor?"
                name="supplier"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={suppliers}
                value={form.supplier}
                errorMessage={errors.supplier}
              />

              <div className="form-group">
                <label
                  htmlFor="inputTrip"
                  className="display-inline-flex flex-wrap justify-content-between width-100pct"
                >
                  Qual a viagem? (OMS)
                </label>
                <input
                  id="inputTrip"
                  type="text"
                  className="form-control"
                  value={form.trip}
                  onChange={(e) => inputChange("trip", e.target.value)}
                  name="trip"
                  placeholder="Digite aqui..."
                />
              </div>

              <InputSelect
                inputChange={inputChange}
                label="Qual o veículo?"
                name="vehicle"
                onCreateNewOption={onCreateNewOption}
                options={vehicles}
                value={form.vehicle}
                errorMessage={errors.vehicle}
              />

              {driversQuantity
                ? Array.from({ length: driversQuantity }).map((_, index) => {
                    return (
                      <InputSelect
                        key={index}
                        inputChange={(_, value) =>
                          index > 0 && value === undefined ? inputClearDriver(index) : inputDriverChange(value, index)
                        }
                        label="Qual o motorista?"
                        name="driver"
                        onCreateNewOption={onCreateNewOption}
                        options={drivers}
                        value={form.driver[index]}
                        errorMessage={errors.driver}
                        onRight={() => setDriversQuantity(driversQuantity + 1)}
                        iconRight="ioglyph rioglyph-plus-light"
                        disabledRight={!form?.driver[index]}
                        useClear={index > 0}
                      />
                    );
                  })
                : null}

              <InputSelect
                inputChange={inputChange}
                label="Qual a categoria?*"
                name="category"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={categories}
                value={form.category}
                errorMessage={errors.category}
              />

              <InputSelect
                disabled={!form.category}
                inputChange={inputChange}
                label="Qual a subcategoria?"
                name="subcategory"
                newOption={newOption}
                onCreateNewOption={onCreateNewOption}
                options={subCategories}
                value={form.subcategory}
                errorMessage={errors.subcategory}
              />

              <div className="form-group ">
                <label htmlFor="inputObservation">Alguma observação?</label>
                <textarea
                  className="form-control"
                  rows={3}
                  id="inputObservation"
                  value={form.observation}
                  onChange={(e) => inputChange("observation", e.target.value)}
                  name="observation"
                  placeholder="Digite aqui..."
                ></textarea>
              </div>
              <div className="form-group margin-top-20">
                <Button
                  onClick={handleButtonClick}
                  iconName="rioglyph rioglyph-upload"
                  className="rounded-small margin-right-5"
                >
                  Anexar comprovante
                </Button>
                {fileName && (
                  <Button bsStyle={Button.MUTED} iconName="rioglyph rioglyph-remove-sign" iconRight onClick={fileClear}>
                    {fileName}
                  </Button>
                )}
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
                {errorFile && <p className="text-color-danger margin-0 margin-top-5">{errorFile}</p>}
              </div>
            </div>
          </fieldset>
        </form>
      </div>
      <div className="display-flex justify-content-between align-items-center  border border-top-only border-width-1 border-color-lighter padding-20">
        <Button onClick={() => openFormModal(false)} className="width-150 rounded-small">
          Cancelar
        </Button>
        <div className="display-flex gap-10">
          {isEdit ? null : (
            <Button onClick={handleSubmitNew} className="width-150 rounded-small">
              Salvar e criar nova
            </Button>
          )}
          <Button onClick={handleSubmit} bsStyle={Button.PRIMARY} className="width-150 rounded-small">
            Salvar
          </Button>
        </div>
      </div>
    </>
  );
};

export default ExpenseForm;
